/**
 * Dove l'app è un sito, e dove invece è un pacchetto.
 *
 * ## La domanda che questa regola risponde
 *
 * Lo stesso `dist/` finisce in cinque posti: il sito su `player.ath.cx`, l'APK Android, il widget
 * Tizen, il pacchetto webOS e il server di sviluppo. Un *service worker* ha senso **solo nel primo**:
 * è quello che rende l'app installabile dalla schermata iniziale di un telefono e che la fa aprire
 * senza rete.
 *
 * ## Perché negli altri quattro sarebbe un danno, non un di più
 *
 * Perché il pacchetto Android **si serve da sé**, su `http://127.0.0.1` (vedi `AssetServer.kt`: è
 * un'origine ordinaria proprio perché lì stanno i dati del cliente). Un service worker lì dentro
 * terrebbe in cache l'involucro di una versione, e dopo l'aggiornamento dell'APK l'`index.html`
 * vecchio chiederebbe file con nomi che non esistono più: schermo bianco, su un apparecchio dove non
 * c'è modo di svuotare niente.
 *
 * Tizen e webOS caricano da `file://`, che non è un contesto sicuro: lì `navigator.serviceWorker`
 * non esiste proprio, e la domanda non si pone. Ma la regola non si appoggia a quell'accidente —
 * dice **dove si registra**, non dove per caso fallirebbe.
 *
 * ## Quel che questa regola costa, detto
 *
 * Che il service worker **non si può provare in locale**: `npm run dev` e `vite preview` servono da
 * loopback, e lì non si registra. È una scelta e non una dimenticanza — durante lo sviluppo un
 * involucro tenuto in cache serve moduli di dieci minuti fa e fa perdere un pomeriggio — e il prezzo
 * è che il comportamento del service worker si prova altrove: le sue regole in `pwa.test.ts`,
 * eseguendo davvero `sw.js` in una stanza finta, e l'installabilità sul sito vero.
 *
 * ## Il cerchio chiuso a mano
 *
 * `https:` e basta non basterebbe: il giorno che l'involucro Android passasse a `https://127.0.0.1`
 * — ed è una cosa che si fa per avere le API che vogliono un contesto sicuro — questa funzione
 * direbbe di sì senza che nessuno abbia cambiato la riga. Il loopback è escluso per nome.
 */

/** Gli indirizzi che vogliono dire «questo pacchetto si serve da solo», o il server di sviluppo. */
const LOOPBACK: ReadonlySet<string> = new Set(['127.0.0.1', 'localhost', '[::1]', '::1']);

/**
 * Vero se questa copia dell'app è un sito, e quindi può installarsi come PWA.
 *
 * Prende protocollo e nome dell'host invece di leggere `location`: così si prova senza un browser, e
 * si provano i casi che su questa macchina non si possono mettere in piedi — `file://` di un
 * televisore Samsung, il loopback dell'APK.
 */
export function shouldRegisterServiceWorker(protocol: string, hostname: string): boolean {
  if (protocol !== 'https:') return false;
  return !LOOPBACK.has(hostname.toLowerCase());
}
